import { useEffect } from 'react'

import { type AuthorPreview } from '@/entities/authors'
import { TrackTypeEnum, trackByIdQuery } from '@/entities/tracks'
import { type UploadFileWithId } from '@/features/upload-files'
import { type TrackTypeEnumEnum } from '@/shared/api'
import { confirm } from '@/shared/lib/antd-utils'
import { hasFormChanged } from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import { ErrorResult, HidingSpin } from '@/shared/ui'
import { Button, Modal } from 'antd'
import { useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { getTrackRequestByFormValues } from './lib/get-track-submit-request'
import {
  $isTrackFormDataPending,
  $isTrackFormModalOpened,
  TrackFormGate,
  trackFormModalClosed,
  trackFormSubmitted,
} from './model/track-form.model'
import { TrackFormBase } from './ui/TrackFormBase'

export {
  trackFormModalOpened,
  trackFormModalClosed,
} from './model/track-form.model'
export const trackSchema = z.object({
  name: z.string(),
  authorId: z.string().uuid().nullable().optional(),
  coauthorIds: z.array(z.string().uuid()).nullable().optional(),
  type: z.custom<TrackTypeEnumEnum>(),
  genreIds: z.array(z.number().int()),
  moodIds: z.array(z.number().int()).optional(),
  albumId: z.string().uuid().nullable().optional(),
  useAlbumPicture: z.boolean(),
  pictureFile: z.custom<UploadFileWithId>().nullable(),
  albumPictureFile: z.custom<UploadFileWithId>().nullable().optional(),
  trackFile: z.custom<UploadFileWithId>().nullable(),
  isArchive: z.boolean().default(false),
})

export type TrackFormModel = z.infer<typeof trackSchema>

interface TrackFormProps {
  isModal?: boolean
  trackTypes?: TrackTypeEnumEnum[]
  initialAuthor?: AuthorPreview
  initialTrackType?: TrackTypeEnumEnum
  onSubmitted?: () => void
}

export function TrackForm({
  isModal = false,
  trackTypes = [],
  initialAuthor,
  initialTrackType,
  onSubmitted,
}: TrackFormProps) {
  // Queries
  const {
    data: track,
    pending: trackPending,
    error: trackError,
  } = useUnit(trackByIdQuery)
  const trackQueryStatus = useUnit(trackByIdQuery.$status)

  const submitForm = useUnit(trackFormSubmitted)
  // Modal
  const isModalOpened = useUnit($isTrackFormModalOpened)
  const closeFormModal = useUnit(trackFormModalClosed)
  // Form

  const formik = useFormik<TrackFormModel>({
    enableReinitialize: true,
    initialValues: track
      ? {
          name: track.name ?? '',
          type: track.type,
          ...(() => {
            const authorIds =
              track.authors
                ?.sort(
                  (a1, a2) => Number(a1.isCoauthor) - Number(a2.isCoauthor),
                )
                .map((a) => a.id) ?? []
            const [authorId, ...coauthorIds] = authorIds
            return {
              authorId: authorId ?? null,
              coauthorIds: coauthorIds,
            }
          })(),
          genreIds: track.genres?.map((g) => g.id) ?? [],
          moodIds: track.moods?.map((g) => g.id) ?? [],
          albumId: track.album?.albumId ?? null,
          pictureFile: track.picture
            ? ({
                id: track.picture.id,
                uid: track.picture.id,
                name: track.picture.name,
                status: 'done',
                url: track.picture.fullCdnUrl,
              } as UploadFileWithId)
            : null,
          useAlbumPicture: track.useAlbumPicture,
          trackFile: track.files?.[0]
            ? ({
                id: track.files[0].id,
                uid: track.files[0].id,
                name: track.files[0].name,
                status: 'done',
                url: track.files[0].fullCdnUrl,
              } as UploadFileWithId)
            : null,
          albumPictureFile: null,
          isArchive: false,
        }
      : {
          name: '',
          type: initialTrackType || trackTypes[0],
          authorId: initialAuthor?.id ?? null,
          coauthorIds: null,
          genreIds: [],
          moodIds: [],
          albumId: null,
          pictureFile: null,
          albumPictureFile: null,
          useAlbumPicture: false,
          trackFile: null,
          isArchive: false,
        },
    validationSchema: toFormikValidationSchema(trackSchema),

    onSubmit: (values: TrackFormModel, { setSubmitting }) => {
      const validate = (): string | null => {
        const errors: string[] = []
        if (!values.type) {
          errors.push('тип трека не выбран')
        } else {
          if (values.type !== TrackTypeEnum.Sermon && !values.genreIds.length) {
            errors.push('жанры не выбраны')
          }
          if (
            !(
              [TrackTypeEnum.Sermon, TrackTypeEnum.AudioBook] as number[]
            ).includes(values.type) &&
            !values.albumId
          ) {
            errors.push('альбом не выбран')
          }
          if (!values.trackFile || values.trackFile.status !== 'done') {
            errors.push('трек не загружен')
          }
          if (
            !values.useAlbumPicture &&
            (!values.pictureFile || values.pictureFile.status !== 'done')
          ) {
            errors.push('обложка не загружена')
          }
          if (values.genreIds && values.genreIds.length > 5) {
            errors.push('максимум 5 жанров')
          }
          if (values.moodIds && values.moodIds.length > 5) {
            errors.push('максимум 5 настроений')
          }
        }
        if (errors.length === 0) {
          return null
        }
        const message = errors.join(', ')
        return message[0].toUpperCase() + message.slice(1)
      }
      const alertDescription = validate()
      if (alertDescription) {
        void showNotificationWarningFx({
          message: 'Ошибка отправки',
          description: alertDescription,
        })
        setSubmitting(false)
        return
      }

      const albumSubmittedData = getTrackRequestByFormValues(values, track)
      void submitForm(albumSubmittedData)
      setSubmitting(false)
    },
  })
  const {
    isValid,
    isValidating,
    isSubmitting,
    resetForm,
    handleSubmit,
    setFieldValue,
    values,
    initialValues,
  } = formik

  useEffect(() => {
    if (!isValid && !isValidating && isSubmitting) {
      void showNotificationWarningFx({ message: 'Не все поля заполнены' })
    }
  }, [isSubmitting, values, isValid, isValidating])

  // Prevent data lose when try to close

  const isDirty = hasFormChanged(formik.values, formik.initialValues, [
    'albumPictureFile',
  ])

  useEffect(() => {
    if (isDirty) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault()
      }
      window.addEventListener('beforeunload', handler)
      return () => {
        window.removeEventListener('beforeunload', handler)
      }
    }
  }, [isDirty])
  const tryToClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: 'Данные не будут сохранены',
        content: 'Вы уверены, что хотите закрыть форму?',
        okText: 'Да, закрыть',
        cancelText: 'Отмена',
        centered: true,
      })
      if (!confirmed) return
    }
    resetForm()
    closeFormModal()
  }

  const formDataPending = useUnit($isTrackFormDataPending)

  // Render
  const formComponent = (() => {
    if (trackError) {
      return (
        <ErrorResult
          error={trackError}
          notFoundConfig={{
            subTitle: 'Трек не найден',
            extra: isModal && (
              <Button onClick={() => void tryToClose()}>Закрыть</Button>
            ),
          }}
        />
      )
    }

    return (
      <>
        <TrackFormGate
          initialAuthor={initialAuthor}
          isModal={isModal}
          resetForm={resetForm}
          onSubmitted={onSubmitted}
        />
        <HidingSpin
          spinning={
            trackQueryStatus !== 'initial' &&
            (formDataPending || (!track && !trackError))
          }
        >
          <TrackFormBase
            isModal={isModal}
            availableTrackTypes={trackTypes}
            initialAuthor={initialAuthor}
            handleSubmit={handleSubmit}
            setFieldValue={setFieldValue}
            values={values}
            initialValues={initialValues}
          />
        </HidingSpin>
      </>
    )
  })()

  return (
    <>
      {isModal ? (
        <Modal
          open={isModalOpened}
          onCancel={() => {
            void tryToClose()
          }}
          destroyOnHidden
          centered
          title={
            trackQueryStatus === 'initial'
              ? 'Создание трека'
              : trackPending || (!track && !trackError)
                ? 'Загрузка'
                : 'Редактирование трека'
          }
          footer={null}
        >
          {formComponent}
        </Modal>
      ) : (
        formComponent
      )}
    </>
  )
}
